import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Identity } from '@dfinity/agent';
import { HttpAgent } from '@dfinity/agent';
import { AuthClient } from '@dfinity/auth-client';
import { Principal } from '@dfinity/principal';

export type AuthMode = 'anonymous' | 'ii' | 'plug' | 'oisy';

export type AuthContextValue = {
  authClient: AuthClient | null;
  mode: AuthMode;
  isAuthenticated: boolean;
  principal: Principal | null;
  agent: HttpAgent | null;
  loginWithII: () => Promise<void>;
  loginWithPlug: () => Promise<void>;
  loginWithOisy: () => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
};

export const AuthContext = createContext<AuthContextValue | null>(null);

const HOST_LOCAL = 'http://localhost:4943';
const HOST_IC = 'https://ic0.app';

function getHost(): string {
  // Check for VITE_DFX_NETWORK first, then fallback to checking if we're on mainnet
  const network = import.meta.env.VITE_DFX_NETWORK || import.meta.env.DFX_NETWORK;
  // If no network env var, assume mainnet if not on localhost
  if (!network) {
    return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' 
      ? HOST_LOCAL 
      : HOST_IC;
  }
  return network === 'ic' ? HOST_IC : HOST_LOCAL;
}

function getIdentityProvider(): string {
  const network = import.meta.env.VITE_DFX_NETWORK || import.meta.env.DFX_NETWORK;
  // If no network env var, assume mainnet if not on localhost
  if (!network) {
    return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
      ? 'http://localhost:4943?canisterId=rdmx6-jaaaa-aaaaa-aaadq-cai'
      : 'https://identity.ic0.app';
  }
  return network === 'ic'
    ? 'https://identity.ic0.app'
    : 'http://localhost:4943?canisterId=rdmx6-jaaaa-aaaaa-aaadq-cai';
}

async function createAgent(identity: Identity | null): Promise<HttpAgent> {
  const agent = new HttpAgent({
    host: getHost(),
    ...(identity ? { identity } : {}),
  });
  const network = import.meta.env.VITE_DFX_NETWORK || import.meta.env.DFX_NETWORK;
  const isLocal = !network || network !== 'ic' || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  if (isLocal) {
    await agent.fetchRootKey();
  }
  return agent;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [authClient, setAuthClient] = useState<AuthClient | null>(null);
  const [mode, setMode] = useState<AuthMode>('anonymous');
  const [principal, setPrincipal] = useState<Principal | null>(null);
  const [agent, setAgent] = useState<HttpAgent | null>(null);

  // Avoid double init in React strict-mode dev
  const didInitRef = useRef(false);

  const isAuthenticated = !!principal && mode !== 'anonymous';

  const syncFromAuthClient = useCallback(
    async (client: AuthClient) => {
      const authed = await client.isAuthenticated();
      if (!authed) {
        setMode('anonymous');
        setPrincipal(null);
        setAgent(await createAgent(null));
        return;
      }
      const identity = client.getIdentity();
      const p = identity.getPrincipal();
      setMode('ii');
      setPrincipal(p);
      setAgent(await createAgent(identity));
    },
    []
  );

  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;

    (async () => {
      const client = await AuthClient.create();
      setAuthClient(client);
      await syncFromAuthClient(client);
    })().catch((err) => {
      // eslint-disable-next-line no-console
      console.error('AuthProvider init failed:', err);
    });
  }, [syncFromAuthClient]);

  const refresh = useCallback(async () => {
    if (!authClient) {
      setAgent(await createAgent(null));
      setMode('anonymous');
      setPrincipal(null);
      return;
    }
    // If we're in II mode, refresh from AuthClient (delegation renewal etc.)
    if (mode === 'ii' || mode === 'anonymous') {
      await syncFromAuthClient(authClient);
    }
  }, [authClient, mode, syncFromAuthClient]);

  const loginWithII = useCallback(async () => {
    if (!authClient) return;
    await authClient.login({
      identityProvider: getIdentityProvider(),
      onSuccess: async () => {
        await syncFromAuthClient(authClient);
      },
    });
  }, [authClient, syncFromAuthClient]);

  const loginWithPlug = useCallback(async () => {
    // Plug exposes window.ic.plug.* in the browser
    const w = window as any;
    const plug = w?.ic?.plug;
    if (!plug) {
      throw new Error('Plug wallet not detected. Please install Plug.');
    }

    // Minimal connect flow; whitelist can be added once canister IDs are finalized.
    await plug.requestConnect({
      host: getHost(),
      whitelist: [],
    });

    // Ensure Plug agent exists
    if (!plug.agent) {
      await plug.createAgent({ host: getHost(), whitelist: [] });
    }

    const principalText: string | undefined =
      typeof plug.getPrincipal === 'function'
        ? (await plug.getPrincipal())?.toText?.()
        : plug.principalId;

    if (!principalText) {
      throw new Error('Plug connected, but could not read principal.');
    }

    setMode('plug');
    setPrincipal(Principal.fromText(principalText));
    // Plug provides an HttpAgent-compatible agent object
    setAgent(plug.agent as HttpAgent);
  }, []);

  const loginWithOisy = useCallback(async () => {
    // OISY integration varies by provider. We support a best-effort window.ic.oisy shape.
    const w = window as any;
    const oisy = w?.ic?.oisy;
    if (!oisy) {
      throw new Error('OISY wallet integration not detected in this browser environment.');
    }

    // Attempt a Plug-like API surface if present
    if (typeof oisy.requestConnect === 'function') {
      await oisy.requestConnect({ host: getHost(), whitelist: [] });
    }
    if (!oisy.agent && typeof oisy.createAgent === 'function') {
      await oisy.createAgent({ host: getHost(), whitelist: [] });
    }

    const principalText: string | undefined =
      typeof oisy.getPrincipal === 'function'
        ? (await oisy.getPrincipal())?.toText?.()
        : oisy.principalId;

    if (!principalText) {
      throw new Error('OISY connected, but could not read principal.');
    }

    setMode('oisy');
    setPrincipal(Principal.fromText(principalText));
    if (oisy.agent) setAgent(oisy.agent as HttpAgent);
    else setAgent(await createAgent(null));
  }, []);

  const logout = useCallback(async () => {
    // Logout II session if present
    if (authClient) {
      try {
        await authClient.logout();
      } catch {
        // ignore
      }
    }

    // Disconnect wallets (best effort)
    const w = window as any;
    if (mode === 'plug' && w?.ic?.plug?.disconnect) {
      try {
        await w.ic.plug.disconnect();
      } catch {
        // ignore
      }
    }
    if (mode === 'oisy' && w?.ic?.oisy?.disconnect) {
      try {
        await w.ic.oisy.disconnect();
      } catch {
        // ignore
      }
    }

    setMode('anonymous');
    setPrincipal(null);
    setAgent(await createAgent(null));
  }, [authClient, mode]);

  const value = useMemo<AuthContextValue>(
    () => ({
      authClient,
      mode,
      isAuthenticated,
      principal,
      agent,
      loginWithII,
      loginWithPlug,
      loginWithOisy,
      logout,
      refresh,
    }),
    [agent, authClient, isAuthenticated, loginWithII, loginWithOisy, loginWithPlug, logout, mode, principal, refresh]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}





